import React, { forwardRef, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

// Helpers
import xor from 'lodash/xor'
import debounce from 'lodash/debounce'

// Components
import FormCheck from 'react-bootstrap/FormCheck'
import Dropdown from 'ui/Dropdown'
import DropdownMenu from 'ui/Dropdown/DropdownMenu'
import DropdownMenuItem from 'ui/Dropdown/DropdownMenuItem'

import SelectToggle from './SelectToggle'

// Styles
import './Select.scss'

const searchDebound = debounce((value, onSearchChange = () => {}) => {
  onSearchChange(value)
}, 300)

export const Select = forwardRef(
  (
    {
      id,
      name,
      value,
      helpText,
      label,
      placeholder,
      options,
      errors,
      multiple,
      loading,
      disabled,
      readOnly,
      searchable,
      deselect,
      color,
      showArrow,
      onBlur,
      onChange,
      onSelect,
      onSearch,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')

    // Validate error
    const error = name in errors ? errors[name] : null
    const showHelpText = error ? false : !!helpText

    // Selected options
    const selectedOptions = useMemo(() => {
      if (Array.isArray(value)) {
        return options.filter((x) => value.includes(x.value))
      }

      if (Array.isArray(options)) {
        return options.filter((x) => x.value === value)
      }

      return []
    }, [options, value])

    // Selected text
    const selectedText = useMemo(() => {
      return selectedOptions.length
        ? selectedOptions.map((x) => x.label).join(', ')
        : placeholder
    }, [placeholder, selectedOptions])

    // Check if given option is selected
    const isOptionSelected = (option) => {
      return selectedOptions.findIndex((x) => x.value === option.value) !== -1
    }

    // On option click
    const onOptionClick = (option) => {
      setOpen(multiple ? true : !open)

      if (Array.isArray(value)) {
        const selectedValues = xor(value, [option.value])
        onChange(selectedValues, name)

        const selectedOptions = options.filter((x) =>
          selectedValues.includes(x.value)
        )
        onSelect(selectedOptions)

        return
      }

      if (deselect === false && option.value === value) {
        return
      }

      onChange(option.value === value ? null : option.value, name)
      onSelect(option.value === value ? null : option, name)
    }

    // On visible change
    const onVisibleChange = (e) => {
      if (e === false) {
        onBlur(name)
      }

      setOpen(e)
    }

    const Menu = () => {
      return (
        <DropdownMenu className="x-select-menu">
          {options.length > 0 ? (
            options.map((option) => {
              return (
                <DropdownMenuItem
                  id={option.label}
                  key={option.value}
                  disabled={option.disabled}
                  className={isOptionSelected(option) ? 'selected' : ''}
                  onClick={() => onOptionClick(option)}
                >
                  <div className={multiple ? 'menu-item-multiple' : ''}>
                    <div className="menu-item-left">
                      {option.label}
                      {option.description && (
                        <div className="menu-item-left--small">
                          {option.description}
                        </div>
                      )}
                    </div>

                    {multiple && (
                      <FormCheck checked={isOptionSelected(option)} readOnly />
                    )}
                  </div>
                </DropdownMenuItem>
              )
            })
          ) : (
            <div className="x-select-empty">No option found</div>
          )}
        </DropdownMenu>
      )
    }

    const onSearchChangeDebound = useCallback(
      (value) => searchDebound(value, onSearch),
      [onSearch]
    )

    const searchChange = useCallback(
      (e) => {
        setSearch(e.target.value)
        onSearchChangeDebound(e.target.value)
      },
      [onSearchChangeDebound]
    )

    return (
      <>
        {label && (
          <label className="x-select-label" htmlFor={name}>
            {label}
          </label>
        )}

        <Dropdown
          overlay={Menu}
          visible={open}
          trigger={['click']}
          disabled={disabled}
          className={{
            disabled: disabled || loading,
            'x-dropdown-select': true,
          }}
          onVisibleChange={onVisibleChange}
        >
          <div>
            <SelectToggle
              id={id}
              ref={ref}
              name={name}
              search={search}
              placeholder={selectedText}
              readOnly={readOnly}
              searchable={searchable}
              color={color}
              loading={loading}
              showArrow={showArrow}
              isSelected={selectedOptions.length > 0}
              isInvalid={!!error}
              onBlur={() => onBlur(name)}
              onSearchChange={searchChange}
            />
          </div>
        </Dropdown>

        {error && <div className="x-select-error">{error}</div>}
        {showHelpText && <div className="x-select-helptext">{helpText}</div>}
      </>
    )
  }
)

Select.displayName = 'Select'

// Props
Select.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  helpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.bool,
    PropTypes.array,
  ]),
  multiple: PropTypes.bool,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  searchable: PropTypes.bool,
  options: PropTypes.array,
  loading: PropTypes.bool,
  searching: PropTypes.bool,
  disabled: PropTypes.bool,
  deselect: PropTypes.bool,
  color: PropTypes.string,
  showArrow: PropTypes.bool,

  // Func
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  onBlur: PropTypes.func,
  onSearch: PropTypes.func,
}

Select.defaultProps = {
  id: '',
  name: '',
  label: '',
  helpText: '',
  placeholder: '',
  value: '',
  multiple: false,
  deselect: true,
  errors: {},
  options: [],
  readOnly: true,
  searchable: false,
  loading: false,
  searching: false,
  disabled: false,
  color: '',
  showArrow: true,
  onChange: () => {},
  onSelect: () => {},
  onBlur: () => {},
  onSearch: () => {},
}

export default Select
